import { useCallback, useEffect, useMemo, useState } from 'react'
import { Search, Users, Receipt, RotateCw } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { type PageSize } from '@/hooks/useCrudList'
import type { PaginationMeta } from '@/types/api'
import { normalizeForMatch } from '@/lib/validators'
import { toast } from '@/components/ui/Toast'
import { CredentialingProvidersService } from '@/services/modules/CredentialingService'
import { currentMonthRange, ReportFilters } from './reportShared'

interface Collaborator { userId: string; userName: string; approvedCount: number; earningsInCents: number; formatted: { earnings: string } }
interface Commission { providerId: string; providerName: string; userId: string | null; userName: string; isPrincipal: boolean; position: number | null; amountInCents: number; approvedAt: string | null; formatted: { amount: string; approvedAt: string } }

type Tab = 'collaborators' | 'commissions'

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const pad2 = (n: number) => String(n).padStart(2, '0')

export function CredentialingReportCommissionsPage() {
  const init = currentMonthRange()
  const now = new Date()
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [tab, setTab] = useState<Tab>('collaborators')
  const [collaborators, setCollaborators] = useState<Collaborator[]>([])
  const [commissions, setCommissions] = useState<Commission[]>([])
  const [loading, setLoading] = useState(false)

  // Recalcular (modal mês/ano). Como o relatório é calculado ao vivo, recalcular
  // = reprocessar a leitura do mês escolhido (reflete comissões recém-vinculadas).
  const [recalcOpen, setRecalcOpen] = useState(false)
  const [recalcMonth, setRecalcMonth] = useState(now.getMonth() + 1)
  const [recalcYear, setRecalcYear] = useState(now.getFullYear())
  const YEARS = useMemo(() => {
    const y = now.getFullYear()
    return [y + 1, y, y - 1, y - 2, y - 3]
  }, [now])

  const load = useCallback(async (f: string, t: string) => {
    if (!f || !t) return
    setLoading(true)
    try {
      const data = await CredentialingProvidersService.reportCommissions(f, t)
      setCollaborators(data.collaborators)
      setCommissions(data.commissions)
    } catch { toast.error('Erro ao carregar comissões.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(init.from, init.to) }, [load, init.from, init.to])

  async function runRecalc() {
    const f = `${recalcYear}-${pad2(recalcMonth)}-01`
    const last = new Date(recalcYear, recalcMonth, 0).getDate()
    const t = `${recalcYear}-${pad2(recalcMonth)}-${pad2(last)}`
    setFrom(f)
    setTo(t)
    await load(f, t)
    setRecalcOpen(false)
    toast.success({ title: 'Comissões recalculadas', description: `Período ${MONTHS[recalcMonth - 1]} de ${recalcYear}.` })
  }

  return (
    <>
      <PageHeader title="Comissões de credenciamento" subtitle="Ganhos por colaborador a partir dos credenciados aceitos"
        crumbs={[{ label: 'Credenciamento' }, { label: 'Relatórios' }, { label: 'Comissões' }]}
        actions={
          <Button variant="secondary" iconLeft={<RotateCw size={14} />} onClick={() => setRecalcOpen(true)}>
            Recalcular comissões
          </Button>
        }
      />
      <ReportFilters from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={() => load(from, to)} loading={loading} />

      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        <TabButton active={tab === 'collaborators'} onClick={() => setTab('collaborators')} icon={<Users size={14} />}>
          Colaboradores
        </TabButton>
        <TabButton active={tab === 'commissions'} onClick={() => setTab('commissions')} icon={<Receipt size={14} />}>
          Comissões
        </TabButton>
      </div>

      {tab === 'collaborators'
        ? <CollaboratorsTab rows={collaborators} loading={loading} />
        : <CommissionsTab rows={commissions} loading={loading} />}

      <Modal
        open={recalcOpen}
        onClose={() => setRecalcOpen(false)}
        title="Recalcular comissões"
        description="Reprocessa as comissões do mês/ano escolhido. Use após vincular a estrutura de comissão ao usuário."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRecalcOpen(false)} disabled={loading}>Cancelar</Button>
            <Button onClick={runRecalc} loading={loading} iconLeft={<RotateCw size={14} />}>Recalcular</Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Mês"
            value={String(recalcMonth)}
            onChange={(e) => setRecalcMonth(Number(e.target.value))}
            options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m.charAt(0).toUpperCase() + m.slice(1) }))}
          />
          <Select
            label="Ano"
            value={String(recalcYear)}
            onChange={(e) => setRecalcYear(Number(e.target.value))}
            options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
          />
        </div>
      </Modal>
    </>
  )
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`cursor-pointer flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
        active ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}>
      {icon}{children}
    </button>
  )
}

// ----------------------------------------------------------------------------
function CollaboratorsTab({ rows, loading }: { rows: Collaborator[]; loading: boolean }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    return q ? rows.filter(r => normalizeForMatch(r.userName).includes(q)) : rows
  }, [rows, search])
  const { paged, meta } = pageOf(filtered, page, pageSize)

  const columns: DataTableColumn<Collaborator>[] = [
    { key: 'name', header: 'Colaborador', alwaysVisible: true, render: r => <span className="font-medium text-gray-800">{r.userName}</span> },
    { key: 'count', header: 'Credenciados aceitos', align: 'center', className: 'w-44', render: r => <Badge variant="green">{r.approvedCount}</Badge> },
    { key: 'earnings', header: 'Total a receber', align: 'right', className: 'w-40', render: r => <span className="font-semibold text-gray-800">{r.formatted.earnings}</span> },
  ]

  const exp = useDataTableExport<Collaborator>({
    filename: 'comissoes-colaboradores',
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: 'Colaborador', accessor: r => r.userName },
      { header: 'Credenciados aceitos', accessor: r => String(r.approvedCount) },
      { header: 'Total a receber', accessor: r => r.formatted.earnings },
    ],
  })

  return (
    <>
      <div className="mb-3 max-w-xs">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          placeholder="Filtrar colaborador" iconLeft={<Search size={14} />} clearable />
      </div>
      <DataTable
        tableId="credentialing.report-collaborators"
        columns={columns} rows={paged} loading={loading} getRowKey={r => r.userId}
        emptyMessage="Nenhum colaborador com credenciados aceitos no período."
        exportable onExportPage={() => exp.exportPage(paged)} onExportAll={exp.exportAll}
      />
      <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />
    </>
  )
}

// ----------------------------------------------------------------------------
function CommissionsTab({ rows, loading }: { rows: Commission[]; loading: boolean }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(25)

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    return q ? rows.filter(r => normalizeForMatch(r.providerName).includes(q) || normalizeForMatch(r.userName).includes(q)) : rows
  }, [rows, search])
  const { paged, meta } = pageOf(filtered, page, pageSize)

  const columns: DataTableColumn<Commission>[] = [
    { key: 'provider', header: 'Credenciado', alwaysVisible: true, render: r => <span className="font-medium text-gray-800">{r.providerName}</span> },
    { key: 'contract', header: 'Contrato', align: 'center', className: 'w-32', render: r => (
      r.isPrincipal
        ? <Badge variant="violet">Principal</Badge>
        : <Badge variant="blue">Vínculo{r.position ? ` #${r.position}` : ''}</Badge>
    ) },
    { key: 'user', header: 'Comissionado', render: r => <span className="text-gray-700">{r.userName}</span> },
    { key: 'approvedAt', header: 'Data da aprovação', align: 'center', className: 'w-44', render: r => <span className="text-gray-600">{r.formatted.approvedAt || '—'}</span> },
    { key: 'amount', header: 'Valor', align: 'right', className: 'w-40', render: r => (
      <span className={r.amountInCents > 0 ? 'font-semibold text-gray-800' : 'text-gray-400'}>{r.formatted.amount}</span>
    ) },
  ]

  const exp = useDataTableExport<Commission>({
    filename: 'comissoes',
    service: { list: async () => ({ data: filtered }) },
    columns: [
      { header: 'Credenciado', accessor: r => r.providerName },
      { header: 'Contrato', accessor: r => r.isPrincipal ? 'Principal' : `Vínculo${r.position ? ` #${r.position}` : ''}` },
      { header: 'Comissionado', accessor: r => r.userName },
      { header: 'Data da aprovação', accessor: r => r.formatted.approvedAt || '' },
      { header: 'Valor', accessor: r => r.formatted.amount },
    ],
  })

  return (
    <>
      <div className="mb-3 max-w-xs">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          placeholder="Filtrar credenciado ou comissionado" iconLeft={<Search size={14} />} clearable />
      </div>
      <DataTable
        tableId="credentialing.report-commissions"
        columns={columns} rows={paged} loading={loading} getRowKey={r => r.providerId}
        emptyMessage="Nenhuma comissão no período."
        exportable onExportPage={() => exp.exportPage(paged)} onExportAll={exp.exportAll}
      />
      <Pagination meta={meta} size={pageSize} onChange={setPage} onSizeChange={setPageSize} />
    </>
  )
}

// paginação local utilitária
function pageOf<T>(filtered: T[], page: number, pageSize: number): { paged: T[]; meta: PaginationMeta } {
  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, lastPage)
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
  const meta: PaginationMeta = {
    current_page: safePage, per_page: pageSize, last_page: lastPage, total: filtered.length,
    from: filtered.length ? (safePage - 1) * pageSize + 1 : null,
    to: Math.min(safePage * pageSize, filtered.length) || null,
  }
  return { paged, meta }
}
